import {ScaleLinear} from 'd3';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {Sequence} from 'components/Sequence';
import {PolymerLayout, SliceMapType} from 'constants/MapsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SequenceType} from 'constants/SequenceConstants';
import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {
  BiointelligentLinkHighlightRegion,
  LINK_HIGHLIGHT_STROKE_OPACITY,
  LINK_HIGHLIGHT_STROKE_WIDTH,
  LINK_NAME_FONT_SIZE,
} from 'molbio/maps/BiointelligentLinksConstants';
import {
  calculateLinkLineLayers,
  getHighlightColor,
  getHighlightOpacity,
  getRectangularPath,
  getTopLineColor,
  isNonOverlappingPartLinkRegion,
} from 'molbio/maps/linear/BiointelligentLinksHelpers';
import ComponentWrapper from 'molbio/maps/linear/ComponentWrapper';
import LegacyNucleotidePartsView from 'molbio/maps/linear/LegacyNucleotidePartsView';
import {useRedraw} from 'molbio/maps/linear/useRedraw';
import {
  PartHighlightRegion,
  splitPartsModelsIntoHighlightRegions,
  splitNucleotideFeatureModelsIntoHighlightRegions,
  splitProteinPartModelsIntoHighlightRegions,
} from 'molbio/maps/translateLinksToHighlightRegions';
import Type from 'molbio/maps/Type';
import Protein from 'molbio/proteins/Protein';

export const TOP_LINE_HEIGHT = 3;
export const TOP_LINE_GAP = 1;
// Number of pixels to show link background above and below the residues
const PROTEIN_PART_RESIDUE_MARGIN = 6;
const VALID_NUCLEOTIDE_LINK_TYPES = [
  BiointelligentLinkType.RNA_PART,
  BiointelligentLinkType.DNA_PART,
  BiointelligentLinkType.TRANSLATION,
  BiointelligentLinkType.TRANSCRIPTION,
];

function isNucleotideSequence(polymer: Sequence | Protein): polymer is Sequence {
  return polymer instanceof Sequence;
}

function validateLinkTypes(polymer: Sequence | Protein, linkTypes: Array<BiointelligentLinkType>): void {
  if (isNucleotideSequence(polymer)) {
    assert(polymer.type() !== SequenceType.OLIGO, 'Biontelligent links on oligos are not supported.');
    assert(
      linkTypes.every((linkType) => VALID_NUCLEOTIDE_LINK_TYPES.includes(linkType)),
      `Found invalid link types for nucleotide sequence: ${linkTypes.join(', ')}.`
    );
  } else {
    assert(
      linkTypes.length === 1 && linkTypes[0] === BiointelligentLinkType.AMINO_ACID_PART,
      `Found invalid link types for AA sequence: ${linkTypes.join(', ')}.`
    );
  }
}

function getBiointelligentLinkRegionsForSlice(
  sliceStart: number,
  sliceEnd: number,
  polymer: Sequence | Protein,
  linkTypes: Array<BiointelligentLinkType>
): Array<BiointelligentLinkHighlightRegion> {
  const polymerLength = polymer.length();
  // Sometimes, we have extra slices past the end of the sequence. This causes warnings when getting the
  // relevant links, so we early return in those cases, and clamp the end such that start/end is always in
  // the [0, seqLength] range.
  if (sliceStart > polymerLength) {
    return [];
  }
  const clampedEnd = Math.min(sliceEnd, polymerLength);
  if (isNucleotideSequence(polymer)) {
    const shouldShowParts =
      linkTypes.includes(BiointelligentLinkType.RNA_PART) ||
      linkTypes.includes(BiointelligentLinkType.DNA_PART);
    const parts = shouldShowParts ? polymer.parts().getSequencePartsBetween(sliceStart, clampedEnd) : [];
    if (!BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS) {
      return splitPartsModelsIntoHighlightRegions(parts, polymer);
    }
    // TODO: Add translation and transcription links.
    return splitNucleotideFeatureModelsIntoHighlightRegions(parts, polymer);
  } else {
    return splitProteinPartModelsIntoHighlightRegions(
      polymer.getPartsBetween(sliceStart, clampedEnd),
      polymer.length()
    );
  }
}

/**
 * Calculates the base height of the highlight region for a nucleotide sequence.
 * This just accounts for the height needed to highlight the residues, and not additional highlighting to
 * fill space between the lines that border the top or bottom of the highlight.
 */
function getHeight(dnaHeight: number, dnaSpacer: number, dnaComplement: boolean): number {
  if (dnaComplement) {
    return 2 * (dnaHeight + dnaSpacer);
  } else {
    return dnaHeight + dnaSpacer;
  }
}

/**
 * Calculates additional highlighting needed to fill space between the colored lines that border the top or
 * bottom of the highlight. When we have overlaps, we can end up with a gap between the top of the highlight
 * and the first line, which looks awkward unless we fill it in.
 *
 * This is the height of the last line in the highlight region, plus the gap between the lines. We first draw
 * the gray highlight all the way up, then draw the colored lines on top of it.
 */
function getAdditionalHeightForLines(
  linkIdToLayer: {[key: string]: number},
  linkRegion: BiointelligentLinkHighlightRegion
): number {
  const lastLineIndex =
    linkRegion.links.length > 0 ? Math.max(...linkRegion.links.map((link) => linkIdToLayer[link.id])) : 0;
  return lastLineIndex * (TOP_LINE_HEIGHT + TOP_LINE_GAP);
}

function getFontType(): Type {
  return new Type(LINK_NAME_FONT_SIZE);
}

function getMaxStackedLinks(linkRegions: Array<BiointelligentLinkHighlightRegion>): number {
  if (linkRegions.length === 0) {
    return 0;
  }
  return _.max(linkRegions.map((region) => region.links.length)) ?? 0;
}

function calculateBoxHeight(polymer: Sequence | Protein, maxStackedLinks: number): number {
  const fontType = getFontType();
  if (isNucleotideSequence(polymer)) {
    return fontType.height() + 2 + maxStackedLinks * (TOP_LINE_HEIGHT + TOP_LINE_GAP);
  } else {
    return TOP_LINE_HEIGHT + PROTEIN_PART_RESIDUE_MARGIN + maxStackedLinks * (TOP_LINE_HEIGHT + TOP_LINE_GAP);
  }
}

/**
 * Calculates the y-position of the colored line above the top of the highlight region for each link.
 *
 * - backgroundY is the highest y-position in our area, so it's our starting point.
 * - From there, we figure out how much to subtract to position the line where we want.
 * - Each line stacked bumps us down by the line's height plus the gap between it and the other lines.
 * - The subtraction of 2 is sort of arbitrary, but it makes everything look a little better.
 */
function calculateLinePosition(backgroundY: number, stackIndex: number): number {
  return backgroundY - (TOP_LINE_HEIGHT + TOP_LINE_GAP) * stackIndex - 2;
}

function calculateLinkDimensions(
  polymerHeight: number,
  polymerSpacer: number,
  complementVisible: boolean,
  maxStackedLinks: number,
  polymerType: ModelTypeConstants
): {
  fontType: Type;
  backgroundY: number;
  height: number;
} {
  const fontType = getFontType();
  switch (polymerType) {
    case ModelTypeConstants.SEQUENCE:
    case ModelTypeConstants.RNA_SEQUENCE:
      // TODO: Currently just highlights both strands. Depending on which strands should be covered by
      // overlapping links, alter height and backgroundY.
      return {
        fontType,
        backgroundY: fontType.height() + 2 + maxStackedLinks * (TOP_LINE_HEIGHT + TOP_LINE_GAP),
        height: getHeight(polymerHeight, polymerSpacer, complementVisible),
      };
    case ModelTypeConstants.PROTEIN:
      return {
        fontType,
        backgroundY: 0,
        height: polymerHeight + polymerSpacer + PROTEIN_PART_RESIDUE_MARGIN,
      };
    default:
      assert(
        false,
        'BiointelligentLinkHighlightView only supports part links on long sequences and proteins.'
      );
  }
}

export interface BiointelligentLinkHighlightViewProps {
  /** A function that takes the index of a residue and returns its x-axis position in pixels on the slice */
  xFn: ScaleLinear<number, number>;
  /** The residue index at which the slice starts */
  sliceStart: number;
  /** The residue index at which the slice ends */
  sliceEnd: number;
  /** The Backbone sequence or protein being rendered */
  polymer: Sequence | Protein;
  /** Flat object layout data */
  polymerLayout: PolymerLayout;
  /** Height in pixels of the residues at default zoom. */
  polymerHeight: number;
  /** Height in pixels of the space below the residues at default zoom. */
  polymerSpacer: number;
  /** Whether or not the complement slice is visible */
  complementVisible: boolean;
  /** What kind of map we're drawing on */
  mapType: SliceMapType;
  /**
   * A function that accepts a new box height or a way to calculate it and triggers a redraw with the new box
   * height.
   * */
  redraw: (newBoxHeight?: number | ((layout: PolymerLayout) => number)) => void;
  /** The types of links being rendered */
  linkTypes: Array<BiointelligentLinkType>;
  /** The coordinates of the link the user is currently hovering over, if any */
  hoveredLinkIndices: {start: number; end: number} | null;
}

/**
 * Responsible for rendering the gray highlight over the residues for a biointelligent link, plus the
 * thin colored lines (not annotations) that border the top or bottom of the highlight.
 *
 * The overall rendering strategy is to draw the gray highlight first, covering both the bases and any
 * additional height from the stacked lines. Then, we draw the colored lines on top of the gray highlight.
 */
export default function BiointelligentLinkHighlightView(
  props: BiointelligentLinkHighlightViewProps
): JSX.Element {
  validateLinkTypes(props.polymer, props.linkTypes);

  const linkRegions = getBiointelligentLinkRegionsForSlice(
    props.sliceStart,
    props.sliceEnd,
    props.polymer,
    props.linkTypes
  );
  const maxStackedLinks = getMaxStackedLinks(linkRegions);

  useRedraw({
    redraw: props.redraw,
    shouldRedraw: () => linkRegions.length > 0,
    newBoxHeight: calculateBoxHeight(props.polymer, maxStackedLinks),
  });

  if (isNucleotideSequence(props.polymer) && !BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS) {
    assert(
      props.linkTypes.length === 1 &&
        [BiointelligentLinkType.RNA_PART, BiointelligentLinkType.DNA_PART].includes(props.linkTypes[0]),
      'New-style parts must be enabled to higlight any link types other than parts in this view.'
    );
    const {
      fontType: partFontType,
      backgroundY,
      height,
    } = calculateLinkDimensions(
      props.polymerHeight,
      props.polymerSpacer,
      props.complementVisible,
      0,
      props.polymer.subtype()
    );
    return (
      <LegacyNucleotidePartsView
        polymerLayout={props.polymerLayout}
        partRegions={linkRegions as Array<PartHighlightRegion>}
        xFn={props.xFn}
        sliceStart={props.sliceStart}
        sliceEnd={props.sliceEnd}
        backgroundY={backgroundY}
        height={height}
        sequenceLength={props.polymer.length()}
        type={partFontType}
        mapType={props.mapType}
      />
    );
  }
  const linkIdToLayer = calculateLinkLineLayers(linkRegions, props.polymer.length());

  return (
    <ComponentWrapper layout={props.polymerLayout} shouldStopMouseDownPropagation={false}>
      <>
        {linkRegions.map((linkRegion: BiointelligentLinkHighlightRegion, index: number) => {
          const {backgroundY, height} = calculateLinkDimensions(
            props.polymerHeight,
            props.polymerSpacer,
            props.complementVisible,
            maxStackedLinks,
            isNucleotideSequence(props.polymer) ? props.polymer.subtype() : ModelTypeConstants.PROTEIN
          );
          const additionalHeight = getAdditionalHeightForLines(linkIdToLayer, linkRegion);
          return (
            <g key={`${props.sliceStart}-${props.sliceEnd}-${index}`}>
              <path
                d={getRectangularPath(
                  linkRegion,
                  props.sliceStart,
                  props.sliceEnd,
                  props.xFn,
                  backgroundY - additionalHeight,
                  height + additionalHeight,
                  props.polymer.length()
                )}
                stroke={getHighlightColor(linkRegion, props.hoveredLinkIndices)}
                strokeWidth={LINK_HIGHLIGHT_STROKE_WIDTH}
                strokeOpacity={LINK_HIGHLIGHT_STROKE_OPACITY}
                fill={getHighlightColor(linkRegion, props.hoveredLinkIndices)}
                fillOpacity={getHighlightOpacity(linkRegion, true)}
              />
              {linkRegion.links.map((link, stackIndex) => (
                <path
                  key={`${props.sliceStart}-${props.sliceEnd}-${index}-${stackIndex}-line`}
                  d={getRectangularPath(
                    linkRegion,
                    props.sliceStart,
                    props.sliceEnd,
                    props.xFn,
                    calculateLinePosition(backgroundY, linkIdToLayer[link.id]),
                    TOP_LINE_HEIGHT,
                    props.polymer.length()
                  )}
                  fill={getTopLineColor(isNonOverlappingPartLinkRegion(linkRegion) && linkRegion.outOfSync)}
                  fillOpacity={getHighlightOpacity(linkRegion, true)}
                />
              ))}
            </g>
          );
        })}
      </>
    </ComponentWrapper>
  );
}
